import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetCases } from "../../../../api/masters/caseAPI";
import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetAreas } from "../../../../api/masters/areaAPI";
import {
  GetIpdEntries,
  GetIpdEntryById,
  UpdateIpdEntry
} from "../../../../api/ipd/ipdEntryAPI";
import { GetConsultantSpecializations } from "../../../../api/masters/consultantSpecializationAPI";
import { GetRooms } from "../../../../api/ipd/roomAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetPaymentReceiveById, UpdatePaymentReceive } from "../../../../api/ipd/paymentReceiveAPI";
import { GetAvailableRooms, GetPreviousRoomChangeHistory, GetRoomChangeById, UpdateRoomChange } from "../../../../api/ipd/roomChangeAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  docType: yup.string().required("Document Type is required"),
  docNo: yup.string().required("Document number is required"),
  // roomId: yup.string().required("Room number is required"),
  ipdEntryId: yup.string().required("Room number is required"),
  newRoomId: yup.string().required("New Room number is required"),
  newDate: yup.date().required("New Date is required"),
  newTime: yup.string().required("New Time is required"),
});

const EditRoomChangeEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [ipdEntries, setIpdEntries] = useState([]);
  const [patientDetails, setPatientDetails] = useState({});
  const [rooms, setRooms] = useState([]);
  const [previousRoomChangeHistory, setPreviousRoomChangeHistory] = useState([]);

  const { id } = useParams();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const fetchIpdEntries = async () => {
    try {
      const response = await GetIpdEntries();
      if (response.success) {
        setIpdEntries(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchAvalableRooms = async () => {
    try {
      const response = await GetAvailableRooms();
      if (response.success) {
        setRooms(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchPreviousRoomChangeHistory = async (ipdEntryId) => {
      try {
        const response = await GetPreviousRoomChangeHistory(ipdEntryId);
        if (response.success) {
          setPreviousRoomChangeHistory(response.data);
        } else {
          showToast("error", response.message);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };

  const fetchData = async () => {
    try {
      const response = await GetRoomChangeById(id); // Make sure login function returns token
      if (response.success) {
        Object.entries(response.data).forEach(([key, value]) => {
          if (["newDate"].includes(key) && value) {
            setValue(key, value.split("T")[0]); // keeps YYYY-MM-DD
          } else {
            setValue(key, value);
          }

          setPatientDetails(response.data?.opdEntryId || response.data?.ipdEntryId || {});
          setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');
          setValue('newRoomId', response.data?.newRoomId?._id || '');
          setValue('roomId', response.data?.roomId?._id || '');
        });
        fetchPreviousRoomChangeHistory(response.data?.ipdEntryId?._id);
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  // Fetch existing data
  useEffect(
    () => {
      if (id) {
        fetchAvalableRooms().then(() => {
          fetchIpdEntries().then(() => {
            fetchData();
          });
        });
      }
    },
    [id, setValue]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdateRoomChange(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Room Change" title="Edit Room Change Entry" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Edit Room Change Entry</h5>
                <Link
                  to={`/ipd/transaction/room-change-entry/list`}
                >
                  <button className="btn btn-primary" id="list">
                    List Room Change Entry
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Room Change Entry
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label>Doc Type *</label>
                      <input className="form-control" {...register("docType")} readOnly/>
                      {errors.docType &&
                        <p className="text-danger">
                          {errors.docType.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Doc No *</label>
                      <input
                        className="form-control"
                        {...register("docNo")}
                        readOnly
                      />
                      {errors.docNo &&
                        <p className="text-danger">
                          {errors.docNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Room No *</label>
                      <select
                        className="form-control"
                        {...register("ipdEntryId",{
                          onChange: (e) => {
                            setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                            setValue("roomId",ipdEntries.find(ipd => ipd._id === e.target.value)?.roomId?._id);
                            fetchPreviousRoomChangeHistory(e.target.value);
                          }
                        })}
                      >
                        <option value="">Select</option>
                        {ipdEntries.map((ipdEntry, index) => (
                          <option key={index} value={ipdEntry._id}>
                            {ipdEntry?.roomId?.roomNo}
                          </option>
                        ))}
                      </select>
                      {errors.ipdEntryId &&
                        <p className="text-danger">
                          {errors.ipdEntryId.message}
                        </p>}
                    </div>

                    <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Patient Details
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>UHID No</label>
                        <input
                          className="form-control"
                          placeholder="UHID No"
                          value={patientDetails?.patientId?.uhidNo}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Ipd No</label>
                        <input
                          className="form-control"
                          placeholder="Ipd No"
                          value={patientDetails?.ipdNo}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Patient</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.patientName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Guardian Name</label>
                        <input
                          className="form-control"
                          placeholder="Guardian Name"
                          value={patientDetails?.patientId?.guardianName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Age</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.age}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Case</label>
                        <input
                          className="form-control"
                          placeholder="Case"
                          value={patientDetails?.caseId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Consultant Doctor</label>
                        <input
                          className="form-control"
                          placeholder="Consultant Doctor"
                          value={patientDetails?.consultantDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>ReferenceDoctor</label>
                        <input
                          className="form-control"
                          placeholder="ReferenceDoctor"
                          value={patientDetails?.referenceDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          className="form-control"
                          placeholder="Address"
                          value={patientDetails?.patientId?.address}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Previous Room Change History
                    </h5>
                  </div>
                  <div className="card">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Pre. Room</th>
                            <th>New Room</th>
                            <th>Change Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previousRoomChangeHistory.map((history, index) => (
                            <tr key={index}>
                              <td>{history?.roomId?.roomNo}</td>
                              <td>{history?.newRoomId?.roomNo}</td>
                              <td>{history?.newDate?.split("T")[0]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  </div>

                   <div className="col-md-4 mb-3">
                      <label>New Room No *</label>
                      <select
                        className="form-control"
                        {...register("newRoomId")}
                      >
                        <option value="">Select</option>
                        {rooms.map((room, index) => (
                          <option key={index} value={room._id}>
                            {room?.roomNo}
                          </option>
                        ))}
                      </select>
                      {errors.newRoomId &&
                        <p className="text-danger">
                          {errors.newRoomId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>New Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        defaultValue={new Date().toISOString().slice(0, 10)}
												// min={new Date().toISOString().slice(0, 10)}
                        {...register("newDate")}
                      />
                      {errors.newDate &&
                        <p className="text-danger">
                          {errors.newDate.message}
                        </p>}
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label>New Time *</label>
                      <input
                        type="time"
                        className="form-control"
                        defaultValue={new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                        {...register("newTime")}
                      />
                      {errors.newTime &&
                        <p className="text-danger">
                          {errors.newTime.message}
                        </p>}
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditRoomChangeEntry;
